import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { UserService } from '../../../service/UserService';
import { useAuthStore } from '../../../store/storeAuth';
import './Payments.scss';
import CreatePaymentMethodModal from './CreatePaymentMethodModal';
import { PaymentInfoDetails } from '../../../util/Types/PaymentInfoDetails';
import { User } from '../../../util/Types/User';
import LoadingSpinner from '../../UI/LoadingSpinner';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputLabel,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { CalendarBlank, Plus, XCircle } from '@phosphor-icons/react';
import { ProductService } from '../../../service/ProductService';
import { Product } from '../../../util/Types/Product';

import PaymentCardBox from './PaymentCardBox';
import { useForm } from 'react-hook-form';
import { CompanyService } from '../../../service/CompanyService';
import KitInput from '../../UI/KitInput';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import Tap from '@tapfiliate/tapfiliate-js';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import { DataGridExportableTable, Invoice } from './DataGridExportableTable';

type Props = {
  authUser: User;
  setSelectedMenuItem?:
    | React.Dispatch<React.SetStateAction<string>>
    | undefined;
};

const Billing = ({ authUser, setSelectedMenuItem }: Props) => {
  const setAuthUser = useAuthStore(state => state.setAuthUser);
  const [onOpen, setOnOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isRetrying, setIsRetrying] = useState(false);

  const [activePlan, setActivePlan] = useState<Product | undefined>(undefined);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(
    authUser.defaultPaymentMethod,
  );
  const setNotification = useNotificationStore(state => state.setNotification);

  const [newMethod, setNewMethod] = useState(true);

  const [methods, setMethods] = useState<PaymentInfoDetails[]>([]);
  const [openPaymentMethods, setOpenPaymentMethods] = useState(false);
  const handleClosePaymentMethods = () => setOpenPaymentMethods(false);

  const {
    handleSubmit: handleSubmitPaymentMethods,
    formState: { errors: submitPaymentErrors },
    register: registerPayment,
    reset,
  } = useForm();

  const stripe = useStripe();
  const elements = useElements();
  const [isPurchased, setIsPurchased] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    readAndAgree: false,
  });

  const [errors, setErrors] = useState({
    name: '',
    readAndAgree: '',
  });

  useEffect(() => {
    const getMyPaymentMethods = async () => {
      setIsLoading(true);
      try {
        const response = await UserService.getPaymentMethods();

        setMethods(response.data);
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: error.message || 'Something went wrong',
        });
      } finally {
        setIsLoading(false);
        setNewMethod(false);
      }
    };
    const getMySubscriptions = async () => {
      setIsLoading(true);
      try {
        const response = await ProductService.getMyProducts();
        const resActivePlan = response?.data?.find(plan => plan.active);
        setActivePlan(resActivePlan);
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: 'Something went wrong',
        });
      } finally {
        setIsLoading(false);
      }
    };

    getMySubscriptions();

    if (newMethod) {
      getMyPaymentMethods();
    }
  }, [newMethod]);

  useEffect(() => {
    const getInvoices = async () => {
      try {
        const res = await ProductService.getProductInvoices();
        setInvoices(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    getInvoices();
  }, []);

  useEffect(() => {
    const createStripeCustomer = async () => {
      try {
        const res = await UserService.createStripeCustomer();
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: error.message || 'Something went wrong',
        });
      }
    };

    if (!authUser.customerId) {
      createStripeCustomer();
    }
  }, [authUser.customerId]);

  if (isLoading) {
    return <LoadingSpinner height="60vh" />;
  }

  const handleSubmit = async () => {
    setModalLoading(true);
    setIsLoading(true);

    if (!formData.readAndAgree) {
      console.log('enters');
      setNotification({
        type: 'error',
        message: 'Please read and agree to the terms and conditions',
      });
      setIsLoading(false);
      setModalLoading(false);
      return;
    }

    if (!stripe || !elements) {
      setNotification({
        type: 'error',
        message: 'Something went wrong',
      });
      setIsLoading(false);
      setModalLoading(false);
      return;
    }

    const card = elements.getElement(CardNumberElement);
    const ccv = elements.getElement(CardCvcElement);
    const expiry = elements.getElement(CardExpiryElement);

    if (!card || !ccv || !expiry) {
      setNotification({
        type: 'error',
        message: 'Something went wrong',
      });
      setIsLoading(false);
      setModalLoading(false);
      return;
    }

    const result = await stripe.createToken(card);

    if (result.error) {
      setNotification({
        type: 'error',
        message: result.error.message || 'Something went wrong',
      });
      setIsLoading(false);
      setModalLoading(false);
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: {
        token: result.token.id,
      },
      billing_details: {
        name: formData.name,
      },
    });

    if (error) {
      setNotification({
        type: 'error',
        message: error.message || 'Something went wrong',
      });
      setIsLoading(false);
      setModalLoading(false);
      return;
    }

    try {
      await UserService.attachPaymentMethod(paymentMethod?.id!, {
        brand: paymentMethod?.card?.brand!,
        exp_month: paymentMethod?.card?.exp_month!,
        exp_year: paymentMethod?.card?.exp_year!,
        last4: paymentMethod?.card?.last4!,
        billing_details: {
          name: paymentMethod?.billing_details?.name!,
        },
      });

      await UserService.setDefaultPaymentMethod(paymentMethod?.id!);

      // Refresh payment methods
      setNewMethod(true);

      // Update authUser with new default payment method
      setAuthUser({
        ...(authUser as User),
        defaultPaymentMethod: paymentMethod?.id!,
      });

      // Clear form data
      setFormData({
        name: '',
        readAndAgree: false,
      });

      handleClosePaymentMethods();
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as any),
      });
    } finally {
      setIsLoading(false);
      setModalLoading(false);
    }
  };

  const onRemovePaymentMethod = async (id: string) => {
    try {
      const response = await UserService.removePaymentMethod(id);

      if (response.status === 'success') {
        setMethods(methods.filter(method => method.paymentMethodId !== id));
      }
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error.message || 'Something went wrong',
      });
    }
  };

  const handleRetry = async (id: string) => {
    setIsRetrying(true);
    try {
      const res = await ProductService.retryPayment({
        id,
      });
      setNotification({
        type: 'success',
        message: res.message,
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: getAPIErrorMessage(error),
      });
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 99,
          backgroundColor: 'white',
          borderBottom: '1px solid #D0D5DD',
          display: 'flex',
          height: authUser.isUserCompany ? '67px' : '64px',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            color: '#000',
            fontFamily: 'Roboto',
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '32px',
            margin: '24px',
          }}
        >
          Billing
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '24px 24px 16px 24px',
          gap: '24px',
        }}
      >
        {/* First Box */}
        <Box
          sx={{
            display: 'flex',
            padding: '24px 24px 0px 24px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '12px',
            alignSelf: 'stretch',
            borderRadius: '8px',
            border: '1px solid #EAECF0',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <Typography
                sx={{
                  color: '#475467',
                  fontFamily: 'Roboto',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                }}
              >
                {activePlan?.name} Plan
              </Typography>
              <Typography
                sx={{
                  color: '#4CAF50',
                  fontFamily: 'Roboto',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                }}
              >
                • Monthly Subscription
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => setSelectedMenuItem?.('pricing')}
            >
              Upgrade
            </Button>
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: '16px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <CalendarBlank size={16} weight="fill" color="#3650DE" />
                <Typography
                  sx={{
                    color: '#3650DE',
                    fontFamily: 'Roboto',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    gap: '8px',
                    lineHeight: '24px',
                  }}
                >
                  Renewal Date
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: '#3650DE',
                  fontFamily: 'Roboto',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  gap: '8px',
                  lineHeight: '24px',
                }}
              >
                {activePlan?.next_charge
                  ? new Date(activePlan.next_charge).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              paddingBottom: '16px',
            }}
          >
            <Typography
              sx={{
                color: '#475467',
                fontFamily: 'Roboto',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '20.02px',
              }}
            >
              {activePlan?.description}
            </Typography>
          </Box>
        </Box>

        {/* Second Box */}
        <Box
          sx={{
            display: 'flex',
            padding: '24px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
            alignSelf: 'stretch',
            borderRadius: '8px',
            border: '1px solid #EAECF0',
            maxHeight: '400px',
            overflowY: methods.length > 3 ? 'auto' : 'hidden',
          }}
        >
          <Typography
            sx={{
              color: '#475467',
              fontFamily: 'Roboto',
              fontSize: '20px',
              fontStyle: 'normal',
              fontWeight: 600,
            }}
          >
            Payment Methods
          </Typography>

          {/* Group of Cards */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            {/* Cards */}
            {methods
              .sort((a, b) =>
                a.paymentMethodId === authUser.defaultPaymentMethod ? -1 : 0,
              )
              .map((card: PaymentInfoDetails) => (
                <PaymentCardBox
                  key={card.id}
                  paymentMethodId={card.paymentMethodId}
                  brand={card.brand}
                  primary={
                    card.paymentMethodId === authUser.defaultPaymentMethod
                  }
                  last4={card.last4}
                  handleDeleteCard={onRemovePaymentMethod}
                />
              ))}
          </Box>

          <Button
            sx={{
              border: '1px solid #3650DE',
              boxShadow: 'none',
              fontSize: '16px',
              fontWeight: 500,
              fontStyle: 'normal',
              padding: '8px 10px',
              backgroundColor: '#fff',
              color: '#3650DE',
            }}
            onClick={() => setOpenPaymentMethods(true)}
          >
            Add Payment Methods
          </Button>
        </Box>

        {/* Invoice Table */}

        <DataGridExportableTable
          rows={invoices}
          authUser={authUser}
          handleRetry={handleRetry}
          isRetrying={isRetrying}
        />
      </Box>

      <CreatePaymentMethodModal
        show={onOpen}
        setAuthUser={setAuthUser}
        authUser={authUser}
        setNewMethod={setNewMethod}
        setDefaultPaymentMethodId={setDefaultPaymentMethodId}
        onHide={() => setOnOpen(false)}
      />

      {/* Add Payment Methods */}
      <Modal
        open={openPaymentMethods}
        onClose={handleClosePaymentMethods}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 4,
            padding: '24px',
            gap: '8px',
          }}
        >
          {/* {isLoading ? (
            <LoadingSpinner height="60vh" />
          ) : ( */}
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
                sx={{ marginBottom: '10px' }}
              >
                Add Payment Method
              </Typography>
              <IconButton onClick={handleClosePaymentMethods}>
                <XCircle size={24} color="#667085" />
              </IconButton>
            </Box>

            <Box
              sx={{
                maxWidth: '500px',
                width: '100%',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <InputLabel>{'Card number'}</InputLabel>
                <CardNumberElement
                  options={{
                    classes: {
                      base: 'base-class-element',
                    },
                    showIcon: true,
                  }}
                />
              </Box>

              <Box sx={{ mt: '20px' }}>
                <TextField
                  type="text"
                  name="name"
                  size="medium"
                  label="Name on Card"
                  value={formData.name}
                  fullWidth
                  onChange={e =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: '20px',
                  marginTop: '20px',
                  flexWrap: 'wrap',
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                  <InputLabel>{'Expiration Date'}</InputLabel>
                  <CardExpiryElement
                    className="base-class-element"
                    options={{
                      classes: {
                        base: 'base-class',
                      },
                      style: {
                        base: {
                          fontSize: '16px',
                          backgroundColor: 'white',
                          padding: '10px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 10px)' } }}>
                  <InputLabel>{'CVC'}</InputLabel>
                  <CardCvcElement
                    className="base-class-element"
                    options={{
                      classes: {
                        base: 'base-class',
                      },
                      style: {
                        base: {
                          fontSize: '16px',
                          backgroundColor: 'white',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              <Typography mt={'20px'} fontSize={'14px'} color={'#101828'}>
                Your personal data will be used to process your order, support
                your experience, and for other purposes described in our Privacy
                Policy.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.readAndAgree}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        readAndAgree: e.target.checked,
                      })
                    }
                    name="readAndAgree"
                  />
                } // Pass the field object to Checkbox
                label={
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color: '#101828',
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '2.2px',
                    }}
                  >
                    I have read and agree to the{' '}
                    <span
                      style={{
                        color: '#3650DE',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        window.open(
                          window.location.origin + '/legal/privacy-policy',
                          '_blank',
                        );
                      }}
                    >
                      Privacy Policy
                    </span>{' '}
                    and
                    <span
                      style={{
                        color: '#3650DE',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        window.open(
                          window.location.origin + '/legal/terms-of-service',
                          '_blank',
                        );
                      }}
                    >
                      {' '}
                      Terms of Service
                    </span>
                  </Typography>
                }
                sx={{}}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type="button"
                variant="outlined"
                sx={{ width: '48%', mt: '20px' }}
                onClick={handleClosePaymentMethods}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={modalLoading}
                sx={{ width: '48%', mt: '20px', gap: '8px' }}
                onClick={handleSubmit}
              >
                {modalLoading ? (
                  <LoadingSpinner height="16px" />
                ) : (
                  <>
                    <Plus size={18} weight="bold" />
                    <Typography>Add</Typography>
                  </>
                )}
              </Button>
            </Box>
          </>
          {/* )} */}
        </Box>
      </Modal>
    </>
  );
};

export default Billing;
